import { Paper, PaperProps } from "@mui/material";
import React, { useEffect } from "react";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";

interface DraggablePaperProps {
    id?: string;
    maxHeight?: number | string;
    centerOnResize?: boolean;
    props?: PaperProps;
}

export const DraggablePaper = ({ id, maxHeight, centerOnResize, props }: DraggablePaperProps) => {
    const widgetContainerRef = React.useRef<HTMLDivElement>(null);
    const [widgetState, setWidgetState] = React.useState({
        position: { x: 0, y: 0 },
        bounds: { left: 0, top: 0, bottom: 0, right: 0 },
    });

    useEffect(() => {
        const handleResize = (_entries) => {
            if (centerOnResize) {
                setWidgetState((old) => ({
                    ...old,
                    position: { x: 0, y: 0 },
                }));
            }
        };

        if (!widgetContainerRef.current) return;

        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(widgetContainerRef.current);
        
        return () => {
            if (widgetContainerRef.current) {
                resizeObserver.unobserve(widgetContainerRef.current);
            } else {
                resizeObserver.disconnect();
            }
        };
    }, [widgetContainerRef.current]);

    const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
        const { clientWidth, clientHeight } = window?.document?.documentElement;
        const targetRect = widgetContainerRef?.current?.getBoundingClientRect();
        if (targetRect) {
            setWidgetState((prevState) => ({
                ...prevState,
                bounds: {
                    left: -targetRect?.left + uiData?.x,
                    right: clientWidth - (targetRect?.right - uiData?.x),
                    top: -targetRect?.top + uiData?.y,
                    bottom: clientHeight - (targetRect?.bottom - uiData?.y),
                },
            }));
        }
    };
    
    const onStop = (_event: DraggableEvent, uiData: DraggableData) => {
        //console.log(uiData);
        setWidgetState((prevState) => ({
            ...prevState,
            position: { x: uiData?.x, y: uiData?.y },
        }));
    };
    
    return (
        <Draggable
            handle={`#${id}`}
            cancel={'[class*="MuiDialogContent-root"]'}
            nodeRef={widgetContainerRef}
            onStart={onStart}
            onStop={onStop}
            position={widgetState.position}
            bounds={widgetState.bounds}
        >
            <Paper ref={widgetContainerRef} {...props} sx={{ maxHeight, height: maxHeight || "auto", overflowX: "hidden" }} />
        </Draggable>
    );
};
